/**
 * HudWidgets.js
 *
 * Phase 6 — Expanded HUD widgets for the 3D gameplay screen.
 * Each widget is a small, pure presentational component driven by the
 * `hudState` snapshot emitted by `GameScene._emit`.
 *
 * Widgets:
 *   - CompassStrip       (top-center)  : bearing dial with player heading + POI ticks
 *   - VitalsStrip        (top-right-ish): HP / Shield / Ammo (Ammo is placeholder)
 *   - AtmospherePanel    (right side)   : atmosphere + O2 timer/bar
 *   - CoordsReadout      (top-left ext.): X/Y/Z + biome label
 *   - MiniRadar          (bottom-right) : polar plot of POIs around the player
 *   - InventoryMiniPanel (top-right ext): credits + cargo % + top ores
 */

import React from 'react';
import {
  Sun, CloudRain, CloudLightning, CloudSnow, Snowflake, Wind, CloudFog, Flame, Biohazard,
} from 'lucide-react';

// --- weather icon / colour maps (shared) ---
const WEATHER_ICON = {
  clear: Sun, mist: CloudFog, toxic_fog: CloudFog,
  rain: CloudRain, thunderstorm: CloudLightning,
  snow: CloudSnow, blizzard: Snowflake,
  dust: Wind, sandstorm: Wind, ashfall: CloudFog,
  ember_storm: Flame, acid_rain: CloudRain, toxic_storm: Biohazard,
};
const WEATHER_COLOR = {
  clear: 'text-amber-300', mist: 'text-slate-300', toxic_fog: 'text-lime-300',
  rain: 'text-sky-300', thunderstorm: 'text-indigo-300',
  snow: 'text-cyan-100', blizzard: 'text-cyan-200',
  dust: 'text-amber-400', sandstorm: 'text-orange-300', ashfall: 'text-zinc-400',
  ember_storm: 'text-orange-400', acid_rain: 'text-lime-400', toxic_storm: 'text-lime-300',
};

// --- shared utilities ---

const cardinalFor = (deg) => {
  const d = ((deg % 360) + 360) % 360;
  if (d < 22.5 || d >= 337.5) return 'N';
  if (d < 67.5) return 'NE';
  if (d < 112.5) return 'E';
  if (d < 157.5) return 'SE';
  if (d < 202.5) return 'S';
  if (d < 247.5) return 'SW';
  if (d < 292.5) return 'W';
  return 'NW';
};

// --- CompassStrip ---

export function CompassStrip({ heading = 0, shipBearing = 0, shipDistance = 0, pois = [] }) {
  // Build a 180° visible strip centered on the current heading.
  // We draw 19 degree ticks (every 10°) and the cardinal labels.
  // POIs are placed by their relative bearing (delta from heading).
  const visibleSpan = 180;
  const half = visibleSpan / 2;
  const norm = (d) => {
    let x = d - heading;
    while (x > 180) x -= 360;
    while (x < -180) x += 360;
    return x;
  };

  const ticks = [];
  for (let off = -half; off <= half; off += 10) {
    const abs = ((heading + off) % 360 + 360) % 360;
    const isMajor = Math.round(abs) % 30 === 0;
    const isCardinal = Math.round(abs) % 90 === 0;
    ticks.push({
      offset: off,
      label: isCardinal ? cardinalFor(abs) : isMajor ? `${Math.round(abs)}` : '',
      isMajor,
      isCardinal,
    });
  }

  const poiTicks = [];
  // Ship as a special POI driven by shipBearing (if provided)
  if (shipDistance > 0) {
    const dx = norm(shipBearing);
    if (Math.abs(dx) <= half) {
      poiTicks.push({ id: 'ship', kind: 'ship', dx, label: `Ship · ${Math.round(shipDistance)}m` });
    }
  }
  // Future P7-P9 pins from `pois` (radar-only for now if they have bearing)
  for (const p of pois) {
    if (p.id === 'ship') continue;
    if (typeof p.bearing === 'number') {
      const dx = norm(p.bearing);
      if (Math.abs(dx) <= half) {
        poiTicks.push({ id: p.id, kind: p.kind, dx, label: p.label || '' });
      }
    }
  }

  const colorFor = (kind) => {
    switch (kind) {
      case 'ship': return 'bg-cyan-300';
      case 'deposit': return 'bg-emerald-300';
      case 'enemy': return 'bg-red-400';
      case 'city': return 'bg-amber-300';
      case 'beacon': return 'bg-yellow-300';
      case 'structure': return 'bg-slate-300';
      case 'cave': return 'bg-violet-400';
      case 'derelict': return 'bg-orange-400';
      default: return 'bg-slate-200';
    }
  };

  return (
    <div
      className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-20 select-none"
      data-testid="hud-compass"
    >
      <div
        className="relative px-3 py-2 rounded-md border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md shadow-[0_0_0_1px_rgba(24,210,255,0.15)]"
        style={{ width: 520, height: 56, fontFamily: 'Source Code Pro, monospace' }}
      >
        {/* Center indicator */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.3em] text-cyan-300/90 pt-0.5"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {Math.round(heading)}° {cardinalFor(heading)}
        </div>
        <div className="absolute top-5 left-1/2 -translate-x-1/2 w-px h-7 bg-cyan-300/80" />

        {/* Tick marks */}
        {ticks.map((t, i) => {
          const x = 50 + (t.offset / half) * 50; // %
          return (
            <div
              key={`tick-${i}`}
              className="absolute"
              style={{ left: `${x}%`, top: '20px', transform: 'translateX(-50%)' }}
            >
              <div
                className={`mx-auto ${t.isCardinal ? 'h-4 bg-cyan-200/90' : t.isMajor ? 'h-3 bg-cyan-300/60' : 'h-2 bg-cyan-300/30'} w-px`}
              />
              {t.label && (
                <div
                  className={`mt-0.5 text-[9px] ${t.isCardinal ? 'text-cyan-100' : 'text-cyan-300/70'} text-center`}
                  style={{ width: 22, marginLeft: -11 }}
                >
                  {t.label}
                </div>
              )}
            </div>
          );
        })}

        {/* POI ticks (above the strip) */}
        {poiTicks.map((p) => {
          const x = 50 + (p.dx / half) * 50;
          return (
            <div
              key={`poi-${p.id}`}
              className="absolute top-1 flex flex-col items-center"
              style={{ left: `${x}%`, transform: 'translateX(-50%)' }}
              data-testid={`hud-compass-poi-${p.kind}`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${colorFor(p.kind)}`} />
              {p.label && (
                <div className="mt-0.5 text-[9px] text-slate-200 whitespace-nowrap px-1 rounded bg-black/40">
                  {p.label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- VitalsStrip ---

export function VitalsStrip({ hp = 100, maxHp = 100, shield = 50, maxShield = 50, ammo = null, maxAmmo = null, reloading = false, reloadProgress = 0, weaponName = null }) {
  const hpPct = Math.max(0, Math.min(1, hp / Math.max(1, maxHp)));
  const shPct = Math.max(0, Math.min(1, shield / Math.max(1, maxShield)));
  const hpColor = hpPct > 0.5 ? 'bg-emerald-400' : hpPct > 0.25 ? 'bg-amber-400' : 'bg-red-500';
  return (
    <div
      className="pointer-events-none absolute bottom-24 left-1/2 -translate-x-1/2 z-20 select-none"
      data-testid="hud-vitals"
    >
      <div
        className="px-4 py-2 rounded-lg border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md flex items-center gap-3"
        style={{ fontFamily: 'Source Code Pro, monospace' }}
      >
        <div className="flex items-center gap-2 min-w-[170px]">
          <span className="text-[10px] tracking-[0.3em] uppercase text-emerald-300/90" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>HP</span>
          <div className="relative w-[120px] h-2 bg-emerald-900/40 rounded overflow-hidden">
            <div className={`h-full ${hpColor} transition-[width] duration-200`} style={{ width: `${hpPct * 100}%` }} />
          </div>
          <span className="text-[10px] tabular-nums text-emerald-100" data-testid="hud-vitals-hp">{Math.round(hp)}</span>
        </div>
        <div className="w-px h-5 bg-cyan-400/20" />
        <div className="flex items-center gap-2 min-w-[170px]">
          <span className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/90" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>SHD</span>
          <div className="relative w-[120px] h-2 bg-cyan-900/40 rounded overflow-hidden">
            <div className="h-full bg-cyan-400 transition-[width] duration-200" style={{ width: `${shPct * 100}%` }} />
          </div>
          <span className="text-[10px] tabular-nums text-cyan-100" data-testid="hud-vitals-shield">{Math.round(shield)}</span>
        </div>
        {ammo !== null && (
          <>
            <div className="w-px h-5 bg-cyan-400/20" />
            <div className="flex items-center gap-2 min-w-[150px]">
              <span className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90" style={{ fontFamily: 'Space Grotesk, sans-serif' }} title={weaponName || ''}>AMMO</span>
              {reloading ? (
                <div className="flex items-center gap-2">
                  <div className="relative w-[60px] h-2 bg-amber-900/40 rounded overflow-hidden">
                    <div className="h-full bg-amber-400" style={{ width: `${Math.min(1, reloadProgress) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-amber-200 tracking-[0.2em] uppercase">Reload</span>
                </div>
              ) : (
                <span className="text-[11px] tabular-nums text-amber-100" data-testid="hud-vitals-ammo">
                  {ammo}{maxAmmo !== null ? ` / ${maxAmmo}` : ''}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --- DamageVignette (red screen edges, fades after a hit) ---
// Pure: a CSS-animated flash (re-keyed per hit) + a static low-HP tint. No
// timers/hooks so it can't trip the react-hooks purity rule.

export function DamageVignette({ lastDamageAt = 0, hp = 100 }) {
  const lowHp = hp < 35;
  return (
    <>
      {lastDamageAt > 0 && (
        <div
          key={lastDamageAt}
          className="damage-flash-anim pointer-events-none fixed inset-0 z-[400]"
          data-testid="damage-vignette"
        />
      )}
      {lowHp && (
        <div
          className="pointer-events-none fixed inset-0 z-[399]"
          style={{ boxShadow: `inset 0 0 220px 60px rgba(220, 38, 38, ${hp < 18 ? 0.4 : 0.25})` }}
          data-testid="lowhp-vignette"
        />
      )}
    </>
  );
}

// --- LightningFlash (full-screen sky flash on a lightning strike) ---
// Re-keyed by `at` so the CSS animation replays on every strike (no setState).

export function LightningFlash({ at = 0 }) {
  if (!at) return null;
  return (
    <div
      key={at}
      className="lightning-flash-anim pointer-events-none fixed inset-0 z-[350] bg-white"
      data-testid="lightning-flash"
    />
  );
}


// --- DeathOverlay (full-screen) ---

export function DeathOverlay({ open, summary, onRespawn, respawning = false }) {  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center"
      data-testid="death-overlay"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative w-[min(520px,92vw)] rounded-2xl border border-red-500/40 bg-[#180808]/95 p-8 text-center shadow-[0_0_0_1px_rgba(239,68,68,0.35),0_30px_60px_rgba(239,68,68,0.25)]">
        <div className="text-[10px] tracking-[0.4em] uppercase text-red-300/90" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Mission Failure
        </div>
        <h2 className="mt-1 text-3xl font-bold text-red-200" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          You Died
        </h2>
        {summary && (
          <div className="mt-3 text-sm text-slate-300 space-y-1" style={{ fontFamily: 'Source Code Pro, monospace' }}>
            <div>Credits lost: <span className="text-red-300 tabular-nums">{summary.credits_lost ?? 0}</span></div>
            <div>Cargo dropped: <span className="text-amber-300">{summary.cargo_count ?? 0} stack(s)</span></div>
            <div className="text-[11px] text-slate-400/80">
              Return to the death site (see Inventory → Death Caches) to recover.
            </div>
          </div>
        )}
        <div className="mt-6">
          <button
            onClick={onRespawn}
            disabled={respawning}
            className="px-6 py-2.5 text-xs tracking-[0.3em] uppercase rounded-lg bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors shadow-[0_0_28px_rgba(24,210,255,0.5)] disabled:opacity-60 disabled:cursor-wait"
            data-testid="death-respawn-button"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            {respawning ? 'Respawning…' : 'Respawn at Ship'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- AtmospherePanel + Oxygen ---

// Estimate a representative surface temperature from biome + habitability.
// Habitability is 0..100 where 100 is Earth-like. We bias by biome type so
// Lava reads hot, Ice cold, Gas Giant chilly upper atmosphere, etc.
function estimateTemperature(bodyType, habitability) {
  const hab = Math.max(0, Math.min(100, Number(habitability) || 0));
  // Per-biome anchor (°C at hab=50) and range.
  const profiles = {
    'Terrestrial': { anchor: 15, range: 60 },
    'Ocean':      { anchor: 12, range: 40 },
    'Lava':       { anchor: 480, range: 220 },
    'Ice Giant':  { anchor: -160, range: 30 },
    'Gas Giant':  { anchor: -90, range: 60 },
    'Dwarf':      { anchor: -120, range: 80 },
    'Moon':       { anchor: -40, range: 180 },
    'Default':    { anchor: 0, range: 120 },
  };
  const p = profiles[bodyType] || profiles.Default;
  // Above hab=50 trends toward anchor; below it pushes toward an extreme.
  const t = (hab - 50) / 50; // -1..+1
  const tempC = Math.round(p.anchor - t * -p.range * 0.6 + (1 - Math.abs(t)) * 0);
  return tempC;
}

// Human-readable atmosphere description used in the info popover.
function describeAtmosphere(atmosphere) {
  const a = String(atmosphere || 'None').toLowerCase();
  switch (a) {
    case 'none':
      return {
        scatter: 'No gas to scatter sunlight — the sky reads near-black with the stars and sun dominating overhead.',
        hazard: 'Vacuum exposure. Suit oxygen required at all times.',
      };
    case 'thin':
      return {
        scatter: 'Wispy gas scatters a faint amount of light, tinting the sky toward the planet\'s base color but leaving stars visible during the day.',
        hazard: 'Insufficient pressure to breathe — suit oxygen recommended.',
      };
    case 'moderate':
      return {
        scatter: 'Standard light scattering paints the sky in the biome\'s base color. Stars wash out near midday but emerge at dusk.',
        hazard: 'Likely breathable if temperature permits.',
      };
    case 'dense':
      return {
        scatter: 'Thick gas saturates the sky in the biome\'s tint and blots out the starfield even at night. Visibility is limited.',
        hazard: 'High pressure. Breathability depends on composition.',
      };
    case 'toxic':
      return {
        scatter: 'Suspended particulates give the sky a sickly greenish veil and dim the sun.',
        hazard: 'Atmospheric chemistry hostile to lungs — keep the suit sealed.',
      };
    default:
      return {
        scatter: 'Atmosphere of unclassified composition.',
        hazard: 'Treat with caution.',
      };
  }
}

export function AtmospherePanel({
  atmosphere = 'None',
  gravity = 9.8,
  oxygen = 100,
  maxOxygen = 100,
  oxygenDepleting = false,
  breathable = true,
  biomeLabel = '',
  bodyType = 'Default',
  habitability = 50,
  weather = null,
}) {
  const oxyPct = Math.max(0, Math.min(1, oxygen / Math.max(1, maxOxygen)));
  const oxyColor =
    oxyPct > 0.5 ? 'bg-sky-400' : oxyPct > 0.25 ? 'bg-amber-400' : 'bg-red-500';
  const [showInfo, setShowInfo] = React.useState(false);
  const tempC = estimateTemperature(bodyType, habitability);
  const tempBand = tempC > 60 ? 'Scorching' : tempC > 35 ? 'Hot' : tempC > 5 ? 'Mild' : tempC > -40 ? 'Cold' : 'Frozen';
  const info = describeAtmosphere(atmosphere);
  const WIcon = (weather && WEATHER_ICON[weather.id]) || Sun;

  return (
    <div
      className="pointer-events-none absolute top-20 right-4 z-20 select-none"
      data-testid="hud-atmosphere"
    >
      <div
        className="px-4 py-3 rounded-xl border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md min-w-[220px]"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/80">Environment</div>
          <button
            type="button"
            onClick={() => setShowInfo((v) => !v)}
            className="pointer-events-auto w-5 h-5 -mr-1 -mt-1 rounded-full border border-cyan-400/40 text-cyan-200/90 text-[10px] leading-none flex items-center justify-center hover:bg-cyan-400/15 transition-colors"
            data-testid="hud-atmosphere-info-toggle"
            aria-label={showInfo ? 'Hide environment details' : 'Show environment details'}
          >
            {showInfo ? '×' : 'i'}
          </button>
        </div>
        <div className="mt-1 text-sm text-slate-100" data-testid="hud-biome-label">{biomeLabel || atmosphere}</div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-slate-300/85" style={{ fontFamily: 'Source Code Pro, monospace' }}>
          <span>ATM: {atmosphere || 'None'}</span>
          <span>GRAV: {Number(gravity).toFixed(1)} m/s²</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-300/85" style={{ fontFamily: 'Source Code Pro, monospace' }}>
          <span data-testid="hud-temp">TEMP: {tempC}°C</span>
          <span className={tempC > 60 || tempC < -40 ? 'text-amber-300/90' : 'text-emerald-300/80'}>{tempBand}</span>
        </div>
        {weather && (
          <div className="mt-2 pt-2 border-t border-cyan-400/10" data-testid="hud-weather">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-[12px] text-slate-100">
                <WIcon className={`w-4 h-4 ${WEATHER_COLOR[weather.id] || 'text-slate-300'}`} />
                {weather.label}
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400" data-testid="hud-weather-severity">
                {weather.severity}
              </span>
            </div>
            <div className="mt-1 w-full h-1 bg-white/10 rounded overflow-hidden">
              <div
                className="h-full bg-cyan-400/70 transition-[width] duration-500"
                style={{ width: `${Math.round((weather.intensity || 0) * 100)}%` }}
              />
            </div>
          </div>
        )}
        <div className="mt-2">
          <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase">
            <span className={breathable ? 'text-emerald-300/80' : 'text-amber-300'}>
              {breathable ? 'Suit · Standby' : 'Suit · O₂ Reserve'}
            </span>
            <span className={`${oxygenDepleting ? 'text-red-300' : 'text-slate-300/80'} tabular-nums`}
              style={{ fontFamily: 'Source Code Pro, monospace' }}
              data-testid="hud-oxygen-pct"
            >
              {Math.round(oxyPct * 100)}%
            </span>
          </div>
          <div className="mt-1 relative w-full h-2 bg-sky-900/40 rounded overflow-hidden">
            <div
              className={`h-full ${oxyColor} transition-[width] duration-300`}
              style={{ width: `${oxyPct * 100}%` }}
            />
          </div>
          {oxygenDepleting && (
            <div className="mt-1 text-[10px] text-red-300/90">Return to ship to replenish</div>
          )}
        </div>
        {showInfo && (
          <div
            className="mt-3 pt-3 border-t border-cyan-400/15 text-[11px] text-slate-200/85 leading-snug space-y-2"
            data-testid="hud-atmosphere-info"
          >
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-300/70 mb-1">Why the sky looks this way</div>
              <p>{info.scatter}</p>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-300/70 mb-1">Surface conditions</div>
              <p>Estimated surface temperature <span className="text-cyan-100">{tempC}°C</span> ({tempBand.toLowerCase()}). Habitability rating <span className="text-cyan-100">{Math.round(habitability)}/100</span>.</p>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-300/70 mb-1">Hazard advisory</div>
              <p className={breathable ? 'text-emerald-200/85' : 'text-amber-200/85'}>{info.hazard}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- CoordsReadout ---

export function CoordsReadout({ coords = { x: 0, y: 0, z: 0 }, bodyName = '' }) {
  return (
    <div
      className="pointer-events-none absolute top-4 left-1/2 -translate-x-[260px] z-20 select-none"
      data-testid="hud-coords"
      style={{ fontFamily: 'Source Code Pro, monospace' }}
    >
      <div className="px-3 py-1.5 rounded-md border border-cyan-400/25 bg-[#02060f]/60 backdrop-blur text-[10px] text-cyan-100/90 tracking-[0.1em]">
        <span className="text-cyan-300/70">X</span> {coords.x.toFixed(0)}{' '}
        <span className="text-cyan-300/70 ml-1">Y</span> {coords.y.toFixed(0)}{' '}
        <span className="text-cyan-300/70 ml-1">Z</span> {coords.z.toFixed(0)}
        {bodyName && <span className="ml-2 text-cyan-300/60">· {bodyName}</span>}
      </div>
    </div>
  );
}

// --- MiniRadar ---

export function MiniRadar({ heading = 0, pois = [], radiusMeters = 80, highlightKinds = [] }) {
  const size = 150;
  const center = size / 2;
  const highlightSet = new Set(highlightKinds || []);
  const colorFor = (kind) => {
    switch (kind) {
      case 'ship': return '#67e8f9';
      case 'deposit': return '#6ee7b7';
      case 'enemy': return '#f87171';
      case 'city': return '#fcd34d';
      case 'beacon': return '#facc15';
      case 'structure': return '#cbd5e1';
      case 'anomaly': return '#5eead4';
      case 'cave': return '#a78bfa';
      case 'derelict': return '#fb923c';
      default: return '#e2e8f0';
    }
  };

  // Find the nearest highlighted POI for the directional arrow.
  let nearestHi = null;
  let nearestDist = Infinity;
  for (const p of pois) {
    if (!highlightSet.has(p.kind)) continue;
    const d = Math.hypot(p.dx ?? 0, p.dz ?? 0);
    if (d < nearestDist) {
      nearestDist = d;
      nearestHi = p;
    }
  }
  // Compute arrow direction (camera-relative, north up).
  let arrowAngleDeg = null;
  if (nearestHi) {
    const dx = nearestHi.dx ?? 0;
    const dz = nearestHi.dz ?? 0;
    const rad = (-heading * Math.PI) / 180;
    // NOTE: screen-x must be +right. dx·cos−dz·sin yields the MIRRORED axis
    // (a POI on the player's right would plot on the left), so negate it.
    const rx = dz * Math.sin(rad) - dx * Math.cos(rad);
    const rz = dx * Math.sin(rad) + dz * Math.cos(rad);
    arrowAngleDeg = (Math.atan2(rx, -rz) * 180) / Math.PI;
  }

  return (
    <div
      className="pointer-events-none absolute bottom-4 right-4 z-20 select-none"
      data-testid="hud-radar"
    >
      {/* Phase 13 — directional arrow + distance to nearest highlighted POI */}
      {nearestHi && (
        <div
          className="mb-1 flex items-center justify-end gap-2 text-[10px] text-teal-200"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          data-testid="hud-radar-waypoint"
        >
          <span className="tracking-[0.2em] uppercase text-teal-300/80">
            {nearestHi.kind} · {Math.round(nearestDist)}m
          </span>
          <svg width="20" height="20" className="block">
            <g transform={`rotate(${arrowAngleDeg}, 10, 10)`}>
              <polygon
                points="10,2 16,18 10,14 4,18"
                fill="#5eead4"
                stroke="#0d9488"
                strokeWidth="1"
              />
            </g>
          </svg>
        </div>
      )}
      <div className="p-2 rounded-xl border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md">
        <svg width={size} height={size} className="block">
          {/* outer ring */}
          <circle cx={center} cy={center} r={center - 2} fill="rgba(2,12,30,0.55)" stroke="rgba(24,210,255,0.35)" strokeWidth="1" />
          {/* inner rings */}
          <circle cx={center} cy={center} r={(center - 2) * 0.66} fill="none" stroke="rgba(24,210,255,0.15)" strokeWidth="1" />
          <circle cx={center} cy={center} r={(center - 2) * 0.33} fill="none" stroke="rgba(24,210,255,0.12)" strokeWidth="1" />
          {/* cross hairs */}
          <line x1={center} y1={2} x2={center} y2={size - 2} stroke="rgba(24,210,255,0.15)" />
          <line x1={2} y1={center} x2={size - 2} y2={center} stroke="rgba(24,210,255,0.15)" />
          {/* N indicator */}
          <text x={center} y={11} textAnchor="middle" fontSize="9" fill="#a5f3fc" fontFamily="Space Grotesk, sans-serif">N</text>
          {/* player triangle (always pointing up) */}
          <polygon points={`${center},${center - 7} ${center - 5},${center + 5} ${center + 5},${center + 5}`} fill="#67e8f9" />
          {/* POIs */}
          {pois.map((p) => {
            const dx = p.dx ?? 0;
            const dz = p.dz ?? 0;
            // Rotate into camera-relative space (player faces up). Screen-x is
            // +right, so rx is negated — without it the radar is left-right
            // mirrored and appears to spin the wrong way as you turn.
            const rad = (-heading * Math.PI) / 180;
            const rx = dz * Math.sin(rad) - dx * Math.cos(rad);
            const rz = dx * Math.sin(rad) + dz * Math.cos(rad);
            // World convention: north is -Z → up on radar = -Z
            const scale = (center - 6) / radiusMeters;
            const px = center + rx * scale;
            const py = center + rz * scale;  // +z plotted downward (south)
            const isHighlighted = highlightSet.has(p.kind);
            const radius = isHighlighted ? 4 : 3;
            // Clamp inside radar
            const dist = Math.hypot(px - center, py - center);
            const maxR = center - 6;
            if (dist > maxR) {
              const k = maxR / dist;
              return (
                <g key={p.id}>
                  {isHighlighted && (
                    <circle
                      cx={center + (px - center) * k}
                      cy={center + (py - center) * k}
                      r={8}
                      fill={colorFor(p.kind)}
                      opacity="0.25"
                    />
                  )}
                  <circle
                    cx={center + (px - center) * k}
                    cy={center + (py - center) * k}
                    r={radius}
                    fill={colorFor(p.kind)}
                    opacity="0.65"
                    data-testid={`radar-poi-${p.kind}-edge`}
                  />
                </g>
              );
            }
            return (
              <g key={p.id}>
                {isHighlighted && (
                  <circle
                    cx={px}
                    cy={py}
                    r={8}
                    fill={colorFor(p.kind)}
                    opacity="0.25"
                  >
                    <animate attributeName="r" values="6;10;6" dur="1.8s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.35;0.1;0.35" dur="1.8s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle
                  cx={px}
                  cy={py}
                  r={radius}
                  fill={colorFor(p.kind)}
                  data-testid={`radar-poi-${p.kind}`}
                />
              </g>
            );
          })}
        </svg>
        <div className="mt-1 text-[9px] tracking-[0.2em] uppercase text-cyan-300/70 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Radar · {radiusMeters}m
        </div>
      </div>
    </div>
  );
}

// --- InventoryMiniPanel ---

export function InventoryMiniPanel({ credits = 0, inventory = null, onOpen }) {
  const ores = inventory?.ores || [];
  const shipCargo = inventory?.ship_cargo || [];
  const cap = inventory?.cargo_capacity || 0;
  const used = ores.reduce((a, o) => a + (o.qty || 0), 0);
  const shipUsed = shipCargo.reduce((a, o) => a + (o.qty || 0), 0);
  const pct = cap > 0 ? used / cap : 0;
  const top = [...ores].sort((a, b) => (b.qty || 0) - (a.qty || 0)).slice(0, 2);

  return (
    <div
      className="absolute top-4 right-4 z-20 select-none"
      data-testid="hud-inventory-mini"
    >
      <div className="px-4 py-3 rounded-xl border border-cyan-400/25 bg-[#02060f]/70 backdrop-blur-md min-w-[220px]"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-300/80">Credits</div>
          <div className="text-base text-amber-300 tabular-nums" data-testid="hud-credits" style={{ fontFamily: 'Source Code Pro, monospace' }}>
            {Number(credits).toLocaleString()}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-cyan-300/80">
          <span>Backpack</span>
          <span className="tabular-nums text-cyan-200" style={{ fontFamily: 'Source Code Pro, monospace' }}>
            {used.toFixed(1)} / {cap.toFixed(0)}
          </span>
        </div>
        <div className="mt-1 relative w-full h-1.5 bg-cyan-900/40 rounded overflow-hidden">
          <div className="h-full bg-cyan-400 transition-[width] duration-200" style={{ width: `${Math.min(1, pct) * 100}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-amber-300/70">
          <span>Ship Cargo</span>
          <span className="tabular-nums text-amber-200" data-testid="hud-ship-cargo" style={{ fontFamily: 'Source Code Pro, monospace' }}>
            {shipUsed.toFixed(1)}
          </span>
        </div>
        {top.length > 0 ? (
          <div className="mt-2 space-y-1 text-[11px] text-slate-200/85" style={{ fontFamily: 'Source Code Pro, monospace' }}>
            {top.map((o) => (
              <div key={o.symbol} className="flex items-center justify-between">
                <span><span className="text-cyan-300/80">{o.symbol}</span> · {o.name}</span>
                <span className="tabular-nums">{(o.qty || 0).toFixed(1)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 text-[11px] text-slate-400/80 italic">Backpack empty</div>
        )}
        {onOpen && (
          <button
            onClick={onOpen}
            className="mt-2 w-full px-2 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-white/5 hover:bg-white/10 border border-cyan-400/25 text-cyan-100 transition-colors"
            data-testid="hud-open-inventory-button"
          >
            Open Inventory
          </button>
        )}
      </div>
    </div>
  );
}



// --- MiningPrompt (reticle-side hint) + MiningProgress bar ---

export function MiningPrompt({ mining = null }) {
  if (!mining) return null;
  const target = mining.target;
  const active = mining.active;
  if (!target && !active) return null;

  const rarity = target?.rarity || mining.target?.rarity;
  const tone =
    rarity === 'Mythic' ? 'text-rose-300' :
    rarity === 'Legendary' ? 'text-amber-300' :
    rarity === 'Ultra Rare' ? 'text-fuchsia-300' :
    rarity === 'Rare' ? 'text-sky-300' :
    rarity === 'Uncommon' ? 'text-emerald-300' :
    'text-slate-200';

  return (
    <div
      className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 mt-8 z-20 select-none"
      data-testid="hud-mining-prompt"
    >
      <div
        className="px-3 py-1.5 rounded-md border border-cyan-400/30 bg-[#02060f]/75 backdrop-blur text-center"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {active ? (
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-200">
            Mining · <span className={tone}>{target?.name || mining.elementName}</span>
          </div>
        ) : (
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-200/90">
            Mineable · <span className={tone}>{target?.name}</span>
            <span className="ml-1 text-slate-400">[{target?.rarity}]</span>
            <span className="ml-2 text-amber-300/90">Hold RMB</span>
          </div>
        )}
        {target?.yield != null && (
          <div className="text-[9px] tracking-[0.2em] uppercase text-slate-400/80 mt-0.5">
            Yield remaining · {target.yield}u
          </div>
        )}
      </div>
    </div>
  );
}

export function MiningProgress({ mining = null }) {
  if (!mining || !mining.active) return null;
  const pct = Math.min(1, Math.max(0, mining.progress || 0));
  return (
    <div
      className="pointer-events-none absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
      data-testid="hud-mining-progress"
    >
      <div className="px-3 py-1.5 rounded-md border border-emerald-400/30 bg-[#02100a]/75 backdrop-blur min-w-[220px]">
        <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-emerald-300/90"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span>Drilling…</span>
          <span className="tabular-nums" style={{ fontFamily: 'Source Code Pro, monospace' }}>{Math.round(pct * 100)}%</span>
        </div>
        <div className="mt-1 relative w-full h-1.5 bg-emerald-900/40 rounded overflow-hidden">
          <div className="h-full bg-emerald-400" style={{ width: `${pct * 100}%` }} />
        </div>
      </div>
    </div>
  );
}

// --- ShipDockPrompt (G to stow cargo into ship) ---

export function ShipDockPrompt({ visible = false, totalUnits = 0, shipCargoUnits = 0, onStow, onRetrieve }) {
  if (!visible) return null;
  return (
    <div
      className="absolute bottom-44 right-4 z-20 select-none"
      data-testid="hud-shipdock-prompt"
    >
      <div
        className="px-3 py-2 rounded-lg border border-amber-400/35 bg-[#1a1206]/85 backdrop-blur shadow-[0_0_20px_rgba(251,191,36,0.18)]"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90">Ship Cargo</div>
        <div className="mt-0.5 text-xs text-slate-200">
          Backpack <span className="text-amber-200 tabular-nums">{Number(totalUnits).toFixed(1)} u</span>
          {shipCargoUnits > 0 && (
            <> · Ship <span className="text-amber-200 tabular-nums">{Number(shipCargoUnits).toFixed(1)} u</span></>
          )}
        </div>
        <button
          onClick={onStow}
          className="mt-2 w-full px-2 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold transition-colors"
          data-testid="hud-shipdock-stow-button"
        >
          Press G · Stow All
        </button>
        {shipCargoUnits > 0 && onRetrieve && (
          <button
            onClick={onRetrieve}
            className="mt-1.5 w-full px-2 py-1 text-[10px] tracking-[0.25em] uppercase rounded border border-amber-300/50 text-amber-200 hover:bg-amber-500/10 transition-colors"
            data-testid="hud-shipdock-retrieve-button"
          >
            Retrieve from Ship
          </button>
        )}
        <div className="mt-1.5 text-[9px] tracking-[0.18em] uppercase text-slate-400">
          Sell at a city kiosk
        </div>
      </div>
    </div>
  );
}

// --- CityEnterPrompt (E to enter colony) ---

export function CityEnterPrompt({ visible = false, cityName = 'Colony', onEnter }) {
  if (!visible) return null;
  return (
    <div
      className="absolute bottom-44 left-1/2 -translate-x-1/2 z-20 select-none"
      data-testid="hud-city-enter-prompt"
    >
      <div
        className="px-4 py-2 rounded-lg border border-amber-300/40 bg-[#120e05]/85 backdrop-blur shadow-[0_0_24px_rgba(252,211,77,0.20)]"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/90 text-center">
          Colony
        </div>
        <div className="mt-0.5 text-sm text-slate-100 text-center" data-testid="hud-city-prompt-name">
          {cityName}
        </div>
        <button
          onClick={onEnter}
          className="mt-2 w-full px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold transition-colors"
          data-testid="hud-city-enter-button"
        >
          Press E · Enter
        </button>
      </div>
    </div>
  );
}
